/**
 * 「继承 / override」的通用纯逻辑,被 work_type(key=code)与 skill(key=code+level)共用。
 *
 * 约定:存储值为空 ⇔ 继承上游(position)的默认值;非空 ⇔ 显式 override。
 * 「显式取空」无法与「继承」区分(空即继承),这是产品决策。
 */

/** 顺序无关的集合相等(按 keyOf 取键)。 */
export function sameSetBy<T>(a: T[], b: T[], keyOf: (t: T) => string): boolean {
  if (a.length !== b.length) return false;
  const bKeys = new Set(b.map(keyOf));
  return a.every((x) => bKeys.has(keyOf(x)));
}

export interface Inherited<T> {
  /** stored 为空 ⇒ 正在继承上游默认值。 */
  inheriting: boolean;
  /** 实际生效的集合(继承时 = inherited 默认值)。 */
  items: T[];
}

/** 把存储值 + 上游默认值解析成「生效集合 + 是否继承」。 */
export function resolveInherited<T>(
  stored: T[] | null | undefined,
  inherited: T[],
): Inherited<T> {
  const inheriting = !stored || stored.length === 0;
  return { inheriting, items: inheriting ? inherited : stored! };
}

/**
 * 把选中集合折算成要持久化的值:
 * - 空 → []（继承）
 * - 与上游默认值相等（未修改,按 keyOf 比较）→ []（保持继承）
 * - 否则 → 选中集合（override）
 */
export function computeStoredInherited<T>(
  selected: T[],
  inherited: T[],
  keyOf: (t: T) => string,
): T[] {
  if (selected.length === 0) return [];
  if (sameSetBy(selected, inherited, keyOf)) return [];
  return selected;
}
